/**
 * Агентский договор и заявки агента на выплату (AD-631, AD-632).
 *
 * Отдельный модуль, а не `referrals.api.ts`: прежний вызов выплаты там
 * (`requestPayout` с `userId` в теле) написан под контракт до AD-632, и
 * совместимость с ним не держится (решение Сергея №71).
 *
 * ФОРМА ОТВЕТОВ. Списки сервер отдаёт как `{ success, data: [...], meta }`
 * (`toPaginatedContract`), одиночные объекты — `{ success, data }`. Чужая форма
 * — ошибка запроса, а не пустой список: пустой список неотличим от «заявок
 * нет», ровно так прятался AD-604. Строки проверяются по полям, от которых
 * экран зависит (`agent-payout-labels.ts`).
 */
import { httpClient } from "../http-client";
import { isPayoutUnavailableReason } from "../referrals/payout-refusal-codes";
import {
  isAgentContractRow,
  isAgentContractStatus,
  isAgentPayoutRequestRow,
  isAgentPayoutSchedule,
  isPayoutRequestStatus,
} from "../referrals/agent-payout-labels";
import type {
  AdminAgentPayoutRequest,
  AgentContract,
  AgentContractStatus,
  AgentKycStatus,
  AgentPayoutAccount,
  AgentPayoutRequest,
  AgentPayoutTransferDetails,
  AgentPayoutsPage,
  CreateAgentContractBody,
  PayoutAvailability,
  PayoutRequestStatus,
  SetAgentPayoutAccountBody,
  UpdateAgentContractBody,
} from "../../types/api/agent-payouts";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nullableString = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

/** Страница списка из `{ data: [...], meta: { total } }`; иначе — ошибка. */
function toPage<T>(
  body: unknown,
  route: string,
  isRow: (row: unknown) => row is T,
): AgentPayoutsPage<T> {
  const rows: unknown = isRecord(body) ? body["data"] : undefined;
  const meta: unknown = isRecord(body) ? body["meta"] : undefined;
  const total: unknown = isRecord(meta) ? meta["total"] : undefined;
  if (!Array.isArray(rows) || typeof total !== "number") {
    throw new Error(
      `${route}: ответ не в форме { data: [...], meta: { total } }`,
    );
  }
  const items = rows.filter(isRow);
  if (items.length !== rows.length) {
    throw new Error(`${route}: строка списка не в ожидаемой форме`);
  }
  return { items, total };
}

/** Объект из `{ success, data: {...} }`, проверенный `isValue`; иначе — ошибка. */
function toObject<T>(
  body: unknown,
  route: string,
  isValue: (value: unknown) => value is T,
): T {
  const data: unknown = isRecord(body) ? body["data"] : undefined;
  if (!isValue(data)) {
    throw new Error(`${route}: ответ не в ожидаемой форме`);
  }
  return data;
}

const isProfile = (value: unknown): value is Record<string, unknown> =>
  isRecord(value);

const isPayoutAccount = (value: unknown): value is AgentPayoutAccount =>
  isRecord(value) &&
  typeof value["contractId"] === "string" &&
  isAgentContractStatus(value["status"]);

const isTransferDetails = (
  value: unknown,
): value is AgentPayoutTransferDetails =>
  isRecord(value) &&
  typeof value["requestId"] === "string" &&
  isPayoutRequestStatus(value["status"]) &&
  typeof value["amount"] === "string" &&
  typeof value["beneficiaryName"] === "string" &&
  typeof value["beneficiaryIban"] === "string";

const isAdminRequestRow = (value: unknown): value is AdminAgentPayoutRequest =>
  isAgentPayoutRequestRow(value);

/**
 * Признак выплаты из профиля. Сервер без AD-632 полей признака не отдаёт —
 * тогда `null`, и экран говорит «нет данных», а не падает (TD-31(б)).
 * Причина, которой экран не знает (сервер новее), читается как `null`: кнопка
 * всё равно скрыта, а текст — общий.
 */
function toPayoutAvailability(
  profile: Record<string, unknown>,
): PayoutAvailability | null {
  const { canRequestPayout, payoutUnavailableReason, payoutCurrency } = profile;
  if (typeof canRequestPayout !== "boolean") return null;
  const schedule = profile["payoutSchedule"];
  return {
    canRequestPayout,
    payoutUnavailableReason: isPayoutUnavailableReason(payoutUnavailableReason)
      ? payoutUnavailableReason
      : null,
    minPayoutAmount: nullableString(profile["minPayoutAmount"]),
    payoutCurrency: payoutCurrency === "EUR" ? payoutCurrency : null,
    payoutSchedule: isAgentPayoutSchedule(schedule) ? schedule : null,
  };
}

export interface PageQuery {
  page: number;
  limit: number;
}

export const agentPayoutsApi = {
  /** Может ли агент подать заявку сейчас — из своего профиля, без `userId`. */
  getPayoutAvailability: async (): Promise<PayoutAvailability | null> => {
    const body: unknown = await httpClient.get<unknown>("/referrals/profile");
    return toPayoutAvailability(
      toObject(body, "GET /referrals/profile", isProfile),
    );
  },

  /** Заявка агента: сумма в EUR, способ — только SEPA, реквизиты из договора. */
  requestPayout: async (amount: number): Promise<AgentPayoutRequest> => {
    const body: unknown = await httpClient.post<unknown>(
      "/referrals/payouts/request",
      { amount, method: "SEPA" },
    );
    return toObject(
      body,
      "POST /referrals/payouts/request",
      isAgentPayoutRequestRow,
    );
  },

  listMyRequests: async (
    query: PageQuery,
  ): Promise<AgentPayoutsPage<AgentPayoutRequest>> => {
    const body: unknown = await httpClient.get<unknown>(
      "/referrals/payouts/requests",
      { params: query },
    );
    return toPage(
      body,
      "GET /referrals/payouts/requests",
      isAgentPayoutRequestRow,
    );
  },

  // ===== Персонал платформы =====

  listRequests: async (
    query: PageQuery & { status?: PayoutRequestStatus },
  ): Promise<AgentPayoutsPage<AdminAgentPayoutRequest>> => {
    const body: unknown = await httpClient.get<unknown>(
      "/referrals/admin/payout-requests",
      { params: query },
    );
    return toPage(
      body,
      "GET /referrals/admin/payout-requests",
      isAdminRequestRow,
    );
  },

  getTransferDetails: async (
    id: string,
  ): Promise<AgentPayoutTransferDetails> => {
    const route = `/referrals/admin/payout-requests/${id}/transfer-details`;
    const body: unknown = await httpClient.get<unknown>(route);
    return toObject(body, `GET ${route}`, isTransferDetails);
  },

  executeRequest: async (id: string): Promise<AgentPayoutRequest> => {
    const route = `/referrals/admin/payout-requests/${id}/execute`;
    const body: unknown = await httpClient.patch<unknown>(route);
    return toObject(body, `PATCH ${route}`, isAgentPayoutRequestRow);
  },

  rejectRequest: async (
    id: string,
    reason: string,
  ): Promise<AgentPayoutRequest> => {
    const route = `/referrals/admin/payout-requests/${id}/reject`;
    const body: unknown = await httpClient.patch<unknown>(route, { reason });
    return toObject(body, `PATCH ${route}`, isAgentPayoutRequestRow);
  },

  markRequestPaid: async (
    id: string,
    transferReference: string,
  ): Promise<AgentPayoutRequest> => {
    const route = `/referrals/admin/payout-requests/${id}/mark-paid`;
    const body: unknown = await httpClient.patch<unknown>(route, {
      transferReference,
    });
    return toObject(body, `PATCH ${route}`, isAgentPayoutRequestRow);
  },
};

/**
 * SHA-256 байтов строчными шестнадцатеричными — форма `documentSha256`.
 * В `digest` уходит представление `Uint8Array`, а не сам `ArrayBuffer`: оба —
 * `BufferSource`, но WebCrypto Node под jsdom отвергает `ArrayBuffer` чужого
 * контекста, и тест экрана не смог бы проверить хеш.
 */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

/** Документ договора: файл, имя для сохранения и SHA-256 полученных байтов. */
export interface AgentContractDocument {
  blob: Blob;
  filename: string;
  sha256: string;
}

export const agentContractsApi = {
  listMyContracts: async (
    query: PageQuery,
  ): Promise<AgentPayoutsPage<AgentContract>> => {
    const body: unknown = await httpClient.get<unknown>("/agent-contracts/my", {
      params: query,
    });
    return toPage(body, "GET /agent-contracts/my", isAgentContractRow);
  },

  /**
   * Документ своего договора. Хеш считается по полученным байтам: принятие
   * отправляет именно его, и сервер сверяет с записанным при отправке на
   * подпись (`document_mismatch` при расхождении) — агент принимает тот файл,
   * который открыл, а не то, что записано в карточке договора.
   */
  downloadMyDocument: async (
    contract: Pick<AgentContract, "id" | "contractNumber">,
  ): Promise<AgentContractDocument> => {
    const { blob, filename } = await httpClient.getBlob(
      `/agent-contracts/my/${contract.id}/document`,
      { expectedType: "application/pdf" },
    );
    return {
      blob,
      filename: filename ?? `agent-contract-${contract.contractNumber}.pdf`,
      sha256: await sha256Hex(await blob.arrayBuffer()),
    };
  },

  acceptContract: async (
    id: string,
    accept: { documentVersion: string; documentSha256: string },
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/my/${id}/accept`;
    const body: unknown = await httpClient.post<unknown>(route, accept);
    return toObject(body, `POST ${route}`, isAgentContractRow);
  },

  // ===== Персонал платформы =====

  listContracts: async (
    query: PageQuery & { status?: AgentContractStatus; agentUserId?: string },
  ): Promise<AgentPayoutsPage<AgentContract>> => {
    const body: unknown = await httpClient.get<unknown>("/agent-contracts", {
      params: query,
    });
    return toPage(body, "GET /agent-contracts", isAgentContractRow);
  },

  createContract: async (
    contract: CreateAgentContractBody,
  ): Promise<AgentContract> => {
    const body: unknown = await httpClient.post<unknown>(
      "/agent-contracts",
      contract,
    );
    return toObject(body, "POST /agent-contracts", isAgentContractRow);
  },

  updateContract: async (
    id: string,
    patch: UpdateAgentContractBody,
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}`;
    const body: unknown = await httpClient.patch<unknown>(route, patch);
    return toObject(body, `PATCH ${route}`, isAgentContractRow);
  },

  getPayoutAccount: async (id: string): Promise<AgentPayoutAccount> => {
    const route = `/agent-contracts/${id}/payout-account`;
    const body: unknown = await httpClient.get<unknown>(route);
    return toObject(body, `GET ${route}`, isPayoutAccount);
  },

  setPayoutAccount: async (
    id: string,
    account: SetAgentPayoutAccountBody,
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}/payout-account`;
    const body: unknown = await httpClient.put<unknown>(route, account);
    return toObject(body, `PUT ${route}`, isAgentContractRow);
  },

  verifyPayoutAccount: async (
    id: string,
    iban: string,
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}/verify-payout-account`;
    const body: unknown = await httpClient.post<unknown>(route, { iban });
    return toObject(body, `POST ${route}`, isAgentContractRow);
  },

  setKycStatus: async (
    id: string,
    kycStatus: AgentKycStatus,
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}/kyc`;
    const body: unknown = await httpClient.patch<unknown>(route, { kycStatus });
    return toObject(body, `PATCH ${route}`, isAgentContractRow);
  },

  sendForSignature: async (id: string): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}/send-for-signature`;
    const body: unknown = await httpClient.post<unknown>(route);
    return toObject(body, `POST ${route}`, isAgentContractRow);
  },

  /** Приостановить, возобновить или расторгнуть — с причиной в журнал. */
  changeStatus: async (
    id: string,
    action: "suspend" | "resume" | "terminate",
    reason: string,
  ): Promise<AgentContract> => {
    const route = `/agent-contracts/${id}/${action}`;
    const body: unknown = await httpClient.post<unknown>(route, { reason });
    return toObject(body, `POST ${route}`, isAgentContractRow);
  },
};
